import React from "react";
import { motion } from "framer-motion";
import {
  PlayCircle,
  ListChecks,
  PhoneCall,
  Users,
  CheckCircle2,
} from "lucide-react";

const steps = [
  {
    number: "1",
    today: true,
    icon: PlayCircle,
    color: "#3b82f6",
    title: "Learn about your office + initial live demos",
    text: "We start by understanding your practice, your team, and your goals — then you see the platform in action today with initial live demos: real workflows, real AI, real data.",
  },
  {
    number: "2",
    icon: ListChecks,
    color: "#6366f1",
    title: "Take home our video learning library and a sandbox to try out",
    bullets: [
      "A sandbox — a demo environment where you can play around with the full platform",
      "Our bite-sized video library, including bite-sized and end-to-end video walkthroughs of the platform",
      "An evaluation checklist",
      "An expectation management guide",
    ],
  },
  {
    number: "3",
    icon: PhoneCall,
    color: "#8b5cf6",
    title: "2nd call — deep-dive demos & answering questions",
    text: "We meet again for deeper, more detailed product demos and answer every question your team brings.",
  },
  {
    number: "4",
    icon: Users,
    color: "#a855f7",
    title: "Talk to a dentist KOL",
    text: "We arrange a call with one of our dentist KOLs who actively uses Asha in their office, so you can learn firsthand how offices are using Asha.",
  },
  {
    number: "5",
    icon: CheckCircle2,
    color: "#d946ef",
    big: true,
    title: "Decision",
    text: (
      <>
        The checklist gets you to a clear yes or no efficiently. Reach a decision{" "}
        <span className="font-semibold text-slate-900">within 14 days of today</span> and we credit
        you{" "}
        <span className="font-semibold text-slate-900">$2,500 toward the platform</span> — apply it
        to your onboarding fee or use it for additional AI credits, for every location you sign up.
      </>
    ),
  },
];

const DeRiskProcessSlide = () => {
  return (
    <div className="h-full w-full bg-slate-50 flex flex-col p-6 xl:p-8 relative overflow-hidden font-sans">
      {/* Background blobs */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-100/40 rounded-full blur-[120px] -translate-y-1/3 translate-x-1/4" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-violet-100/40 rounded-full blur-[100px] translate-y-1/3 -translate-x-1/4" />
      <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:32px_32px] opacity-40" />

      <div className="max-w-[1120px] w-full mx-auto flex flex-col h-full relative z-10 justify-center">
        {/* Header */}
        <div className="text-center mb-7 flex-shrink-0">
          <motion.h2
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-3xl lg:text-[2.6rem] font-extrabold text-slate-900 tracking-tight leading-[1.05]"
          >
            Our{" "}
            <span className="bg-gradient-to-r from-blue-600 via-indigo-600 to-fuchsia-600 bg-clip-text text-transparent">
              Match Process
            </span>
          </motion.h2>
        </div>

        {/* Vertical timeline */}
        <div className="relative max-w-[920px] mx-auto w-full">
          {/* The gradient connector line — sits behind the number bubbles */}
          <div
            className="absolute left-[27px] top-6 bottom-6 w-[2px] rounded-full"
            style={{
              background:
                "linear-gradient(to bottom, #3b82f6 0%, #6366f1 25%, #8b5cf6 55%, #a855f7 80%, #d946ef 100%)",
              opacity: 0.5,
            }}
          />

          <div className="space-y-2.5">
            {steps.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -16 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.25 + i * 0.07, duration: 0.45, ease: "easeOut" }}
                  className="relative flex items-stretch gap-5 group"
                >
                  {/* Number bubble */}
                  <div className="relative flex-shrink-0 w-[56px] flex items-start justify-center pt-1">
                    <div
                      className="relative w-[44px] h-[44px] rounded-full flex items-center justify-center font-extrabold text-white text-[15px] shadow-lg ring-4 ring-slate-50"
                      style={{
                        background: `linear-gradient(135deg, ${s.color} 0%, ${s.color}dd 100%)`,
                        boxShadow: `0 8px 20px -6px ${s.color}55, 0 0 0 1px ${s.color}40 inset`,
                      }}
                    >
                      {s.number}
                    </div>
                  </div>

                  {/* Card */}
                  <motion.div
                    whileHover={{ y: -1 }}
                    className="flex-1 bg-white/90 backdrop-blur-sm rounded-xl border border-slate-200/80 px-5 py-3.5 flex items-center gap-4 shadow-[0_4px_20px_-8px_rgba(15,23,42,0.08)] hover:shadow-[0_12px_32px_-12px_rgba(15,23,42,0.14)] transition-shadow"
                  >
                    {/* Icon chip */}
                    <div className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center bg-slate-100 text-slate-500">
                      <Icon className="w-[18px] h-[18px]" strokeWidth={2.2} />
                    </div>

                    {/* Title + description */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <h3 className="text-[15px] lg:text-[16px] font-bold text-slate-900 leading-tight">
                          {s.title}
                        </h3>
                        {s.today && (
                          <span className="text-[9.5px] font-bold tracking-[0.12em] uppercase px-1.5 py-0.5 rounded bg-blue-50 text-blue-600">
                            Today
                          </span>
                        )}
                      </div>
                      {s.bullets ? (
                        <ul className="mt-1 space-y-1">
                          {s.bullets.map((b) => {
                            const isGroup = typeof b === "object";
                            const label = isGroup ? b.text : b;
                            return (
                              <li
                                key={label}
                                className="text-[12px] lg:text-[12.5px] text-slate-600 leading-[1.45]"
                              >
                                <div className="flex items-start gap-2">
                                  <span
                                    className="mt-[6px] w-1.5 h-1.5 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: s.color }}
                                  />
                                  <span className={isGroup ? "font-semibold text-slate-700" : ""}>
                                    {label}
                                  </span>
                                </div>
                                {isGroup && b.sub && (
                                  <ul className="mt-1 ml-4 space-y-1">
                                    {b.sub.map((sb) => (
                                      <li key={sb} className="flex items-start gap-2">
                                        <span className="mt-[6px] w-1 h-1 rounded-full flex-shrink-0 bg-slate-300" />
                                        <span>{sb}</span>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      ) : (
                        <p
                          className={
                            s.big
                              ? "text-[14px] lg:text-[15px] text-slate-700 leading-[1.5]"
                              : "text-[12.5px] lg:text-[13px] text-slate-600 leading-[1.5]"
                          }
                        >
                          {s.text}
                        </p>
                      )}
                    </div>
                  </motion.div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeRiskProcessSlide;
